/* eslint-disable react/display-name */
import React, { FC } from 'react'
import { Hit, InfiniteHitsProvided } from 'react-instantsearch-core'
import { Highlight, connectInfiniteHits } from 'react-instantsearch-dom'

import { JournalCardSearchEatable } from '../JournalCardSearchEatable'
import { JournalCardSearchMotion } from '../JournalCardSearchMotion'
import { CustomStats } from '../Search/searchHelpers'

import { AddJournalEatableRecordHandler } from './types'

import { LoadingIndicator } from 'Components/LoadingIndicator'
import { EatableType, Food, Motion, MotionLevelObject, Recipe } from 'Models'
import { useInfiniteScrollSentinel } from 'Utils/helpers/useInfiniteScrollSentinel'

type SearchJournalHitProps<THit> = {
  hitComponent: FC<{ hit: THit }>
  hitName: { singular: string; plural: string }
}

const SearchJournalHit = (connectInfiniteHits(
  ({
    hits,
    hasMore,
    refineNext,
    hitComponent: HitComponent,
    hitName
  }: InfiniteHitsProvided & SearchJournalHitProps<Hit>) => {
    const { sentinelRef, isLoadingMore } = useInfiniteScrollSentinel({
      hasMore,
      onLoadMore: refineNext,
      resetKey: hits.length
    })

    return (
      <>
        <div className="search-hits-count">
          <CustomStats hitName={hitName} />
        </div>

        <div className="ais-InfiniteHits">
          <ul className="ais-InfiniteHits-list">
            {hits.map((hit) => (
              <li key={hit.objectID} className="ais-InfiniteHits-item">
                <HitComponent hit={hit} />
              </li>
            ))}
          </ul>

          {hasMore && <div ref={sentinelRef} aria-hidden="true" />}

          {isLoadingMore && (
            <div className="mt-3 mb-3">
              <LoadingIndicator />
            </div>
          )}
        </div>
      </>
    )
  }
) as unknown) as <THit>(props: SearchJournalHitProps<THit>) => JSX.Element

export { SearchJournalHit }

export type EatableProps = {
  eatableType: EatableType
  initialDate: Date
  addJournalEatableRecord: AddJournalEatableRecordHandler
  onSaveSearchResult: () => void
}

export const eatableHit = ({
  eatableType,
  initialDate,
  addJournalEatableRecord,
  onSaveSearchResult
}: EatableProps): FC<{ hit: Food | Recipe }> => ({ hit }) => (
  <JournalCardSearchEatable
    initialDate={initialDate}
    quantifiedEatable={{ [eatableType.toLowerCase()]: hit }}
    renderTitle={() => <Highlight attribute="name" tagName="mark" hit={hit} />}
    onSave={(eatable, date, mealCategory) => {
      addJournalEatableRecord(eatable, mealCategory, date, 'search')
      onSaveSearchResult()
    }}
  />
)

type AddJournalMotionRecordHandler = (motion: Motion, duration: number, motionlevelId: string, date?: Date) => void

type PropsMotion = {
  addJournalMotionRecord: AddJournalMotionRecordHandler
  weight: number
  date: Date
  onSaveSearchResult: () => void
}

export const motionHit = ({
  addJournalMotionRecord,
  weight,
  date,
  onSaveSearchResult
}: PropsMotion): FC<{ hit: Motion }> => ({ hit }) => (
  <JournalCardSearchMotion
    motion={hit}
    initialDate={date}
    weight={weight}
    renderTitle={() => <Highlight attribute="name" tagName="mark" hit={hit} />}
    onSave={(motion, duration, nextDate, category) => {
      addJournalMotionRecord(motion, duration, (category || ({} as MotionLevelObject)).id, nextDate)
      onSaveSearchResult()
    }}
  />
)
